import {
  normalize_text,
  read_safe_object_value,
  write_safe_object_value
} from '@/services/onboarding_form_helpers';
import { reset_file_input_value } from './queue_form_method_shared';

export const queueFormFileMethods = {
  get_facial_verification_value(question_id) {
    return read_safe_object_value(this.facial_verification_values, question_id, null);
  },
  update_facial_verification_value(question_id, value) {
    write_safe_object_value(this.facial_verification_values, question_id, value);
  },
  update_multi_select_value(question_id, option_value, is_checked) {
    const current_value = read_safe_object_value(this.form_values, question_id, []);
    const existing_values = Array.isArray(current_value) ? [...current_value] : [];
    if (is_checked) {
      if (!existing_values.includes(option_value)) existing_values.push(option_value);
    } else {
      const option_index = existing_values.indexOf(option_value);
      if (option_index >= 0) existing_values.splice(option_index, 1);
    }
    write_safe_object_value(this.form_values, question_id, existing_values);
  },
  handle_file_change(question_id, event) {
    const selected_files = Array.from(event?.target?.files || []).map((file) => {
      Object.defineProperty(file, '_preview_url', {
        value: URL.createObjectURL(file),
        configurable: true
      });
      return file;
    });
    const existing_files = read_safe_object_value(this.file_values, question_id, []);
    const next_files = [
      ...(Array.isArray(existing_files) ? existing_files.filter((file) => !file?.removed) : existing_files ? [existing_files] : []),
      ...selected_files
    ];
    write_safe_object_value(this.file_values, question_id, next_files);
    write_safe_object_value(
      this.file_previews,
      question_id,
      next_files.map((file) => file?._preview_url).filter(Boolean)
    );
    if (event?.target) event.target.value = '';
  },
  visible_question_files(question_id) {
    const files = read_safe_object_value(this.file_values, question_id, []);
    return (Array.isArray(files) ? files : files ? [files] : []).filter((file) => file && !file.removed);
  },
  remove_question_file(question_id, file_index) {
    const files = read_safe_object_value(this.file_values, question_id, []);
    const next_files = Array.isArray(files) ? [...files] : files ? [files] : [];
    const target_file = Reflect.get(this.visible_question_files(question_id), file_index);
    const target_index = next_files.indexOf(target_file);
    if (!target_file || target_index < 0) return;

    if (target_file.persisted) {
      Reflect.set(next_files, target_index, { ...target_file, removed: true });
    } else {
      if (target_file._preview_url) URL.revokeObjectURL(target_file._preview_url);
      next_files.splice(target_index, 1);
    }

    write_safe_object_value(this.file_values, question_id, next_files);
    write_safe_object_value(
      this.file_previews,
      question_id,
      next_files.map((file) => file?._preview_url).filter(Boolean)
    );
  },
  format_file_name(file) {
    return file?.name || 'Uploaded file';
  },
  file_input_label(question_id) {
    const count = this.visible_question_files(question_id).length;
    if (!count) return 'Choose files';
    return `${count} ${count === 1 ? 'file' : 'files'} selected`;
  },
  clear_selected_file(question_id) {
    const existing_preview = read_safe_object_value(this.file_previews, question_id, '');
    if (existing_preview) URL.revokeObjectURL(existing_preview);
    write_safe_object_value(this.file_values, question_id, null);
    write_safe_object_value(this.file_previews, question_id, '');
    reset_file_input_value(question_id);
    if (this.preview_modal_visible && String(this.preview_modal_url || '') === String(existing_preview || '')) {
      this.close_preview_modal();
    }
  },
  reset_file_previews() {
    Object.values(this.file_previews)
      .flatMap((preview_url) => (Array.isArray(preview_url) ? preview_url : [preview_url]))
      .forEach((preview_url) => {
        if (preview_url) URL.revokeObjectURL(preview_url);
      });
    this.file_previews = {};
  },
  file_preview_url(question_id, file = null) {
    if (file?.url) return file.url;
    if (file?._preview_url) return file._preview_url;
    const preview_urls = read_safe_object_value(this.file_previews, question_id, []);
    return (Array.isArray(preview_urls) ? preview_urls[0] : preview_urls) || '';
  },
  is_image_file(file) {
    return Boolean(file?.type?.startsWith('image/')) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(file?.name || file?.url || ''));
  },
  is_pdf_file(file) {
    return file?.type === 'application/pdf' || /\.pdf($|\?)/i.test(String(file?.name || file?.url || ''));
  },
  open_preview_modal(question, file = null) {
    const selected_file = file || this.visible_question_files(question?.id)[0] || null;
    const preview_url = this.file_preview_url(question?.id, selected_file);
    if (!selected_file || !preview_url) return;

    this.preview_modal_title = question?.label || this.format_file_name(selected_file);
    this.preview_modal_url = preview_url;
    this.preview_modal_type = this.is_image_file(selected_file) ? 'image' : this.is_pdf_file(selected_file) ? 'pdf' : 'file';
    this.preview_modal_visible = true;
  },
  open_history_document_preview(document = {}) {
    const preview_url = String(document?.url || '').trim();
    if (!preview_url) return;

    const preview_file = {
      name: document?.file_name || document?.label || 'Uploaded document',
      url: preview_url,
      type: String(document?.content_type || document?.mime_type || '')
    };
    this.preview_modal_title = document?.label || preview_file.name;
    this.preview_modal_url = preview_url;
    this.preview_modal_type = this.is_image_file(preview_file)
      ? 'image'
      : this.is_pdf_file(preview_file)
        ? 'pdf'
        : 'file';
    this.preview_modal_visible = true;
  },
  preview_modal_src() {
    if (!this.preview_modal_url) return '';
    return this.preview_modal_type === 'pdf'
      ? `${this.preview_modal_url}#toolbar=1&navpanes=0&view=FitH`
      : this.preview_modal_url;
  },
  close_preview_modal() {
    this.preview_modal_visible = false;
    this.preview_modal_title = '';
    this.preview_modal_url = '';
    this.preview_modal_type = '';
  },
  is_history_document_review_supported(document = {}) {
    const source = normalize_text(document?.source);
    if (source === 'application') {
      return Number(document?.application_form_id || 0) > 0
        && Number(document?.section_id || 0) > 0
        && Number(document?.question_id || 0) > 0;
    }
    if (source === 'additional') {
      return Number(document?.id || 0) > 0;
    }
    return false;
  },
  unsupported_history_document_review_reason(document = {}) {
    const source = normalize_text(document?.source);
    if (!source) return 'This document source cannot be reviewed from this card yet.';
    if (source === 'application' || source === 'additional') return '';
    if (source === 'submission') return 'Signed documents are view-only here and are not manually approved from this card.';
    if (source === 'journal') return 'Journal documents are informational here and are not manually approved from this card.';
    if (source === 'kyc_report') return 'KYC reports are generated outputs and are not manually approved from this card.';
    return 'This document source cannot be reviewed from this card yet.';
  }
};
